import type {
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
  FlightItinerarySlice,
} from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";

interface UseFlightDetailsPanelProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  airlineMap?: Record<string, { displayName: string }>;
}

export const useFlightDetailsPanel = ({
  itinerary,
  airlineMap,
}: UseFlightDetailsPanelProps) => {
  const { t } = useI18nContext();
  const slices = itinerary.bookedItinerary?.travelItinerary?.slices ?? [];

  const allSegments = slices.flatMap(
    (slice) => slice.segments ?? []
  ) as FlightItinerarySegment[];

  const tripOriginCode = slices[0]?.segments?.[0]?.origin?.locationCode ?? "";

  const sliceSummaries = slices.map(
    (slice: FlightItinerarySlice, index: number) => {
      const segs = slice.segments ?? [];
      const firstSegment = segs[0];
      const lastSegment = segs[segs.length - 1];
      if (!firstSegment || !lastSegment) {
        return null;
      }
      const departureStr =
        firstSegment.zonedScheduledDeparture ??
        firstSegment.scheduledDeparture ??
        "";
      const arrivalStr =
        lastSegment.zonedScheduledArrival ?? lastSegment.scheduledArrival ?? "";
      const totalDurationMinutes =
        departureStr && arrivalStr
          ? dayjs(arrivalStr).diff(dayjs(departureStr), "minutes")
          : 0;
      const isReturn = index > 0;
      return {
        firstSegment,
        lastSegment,
        totalDurationMinutes,
        stopsCount: Math.max(0, segs.length - 1),
        isReturn,
        displayDestinationCode: isReturn ? tripOriginCode : undefined,
      };
    }
  );

  const operatingCarrier = (() => {
    const firstSegment = allSegments[0];
    if (!firstSegment) return "";
    const code =
      firstSegment.operatingAirline?.code ??
      firstSegment.marketingAirline?.code;
    return airlineMap?.[code ?? ""]?.displayName ?? code ?? "";
  })();

  const hasLayover = slices.some((slice) => (slice.segments?.length ?? 0) > 1);

  const uniqueAirlineCodes = new Set(
    allSegments
      .map((seg) => seg.operatingAirline?.code ?? seg.marketingAirline?.code)
      .filter((code): code is string => Boolean(code))
  );
  const hasMultipleOperatingCarriers = uniqueAirlineCodes.size > 1;

  const title = hasMultipleOperatingCarriers
    ? t("core-flights.operatedByMultipleAirlines")
    : t("operatedBy", { airline: operatingCarrier });

  return {
    sliceSummaries,
    hasLayover,
    title,
  };
};
